import type { InferOutput } from 'valibot'
import { consola } from 'consola'
import {
  array,
  digits,
  fallback,
  minValue,
  nonEmpty,
  object,
  optional,
  picklist,
  pipe,
  safeParse,
  string,
  summarize,
  transform,
} from 'valibot'

/** Characters to strip from search queries (Solr special syntax characters) */
const STRIP_SEARCH_CHARACTERS = /[*@{}[\]~^:;!()\\|]/g

/**
 * Filter tags for Solr tag/exclude pattern (multi-select faceting)
 * Used with {!tag=X} for filters and {!ex=X} for facet exclusions
 *
 * @see https://solr.apache.org/guide/solr/latest/query-guide/faceting.html#tagging-and-excluding-filters
 */
export const FILTER_FACET_TAGS = {
  nodeType: 'nt',
  legalArea: 'la',
  author: 'au',
} as const satisfies Record<string, string>

const nonBlankString = fallback(optional(pipe(string(), nonEmpty())), undefined)

// Parses JSON string to array, e.g. '["foo","bar"]' -> ["foo","bar"]
const jsonStringArray = fallback(
  optional(
    pipe(
      string(),
      transform((val) => {
        try {
          return JSON.parse(val)
        }
        catch {
          return undefined
        }
      }),
      array(string()),
      nonEmpty(),
    ),
  ),
  undefined,
)

const positiveInteger = fallback(optional(pipe(string(), digits(), transform(Number), minValue(1))), 1)

export const searchUrlSchema = object({
  sq: nonBlankString,
  page: positiveInteger,
  contentTypes: jsonStringArray,
  legalAreas: jsonStringArray,
  authors: jsonStringArray,
  startYear: nonBlankString,
  endYear: nonBlankString,
  sort: pipe(
    nonBlankString,
    optional(picklist(['relevance', 'newest', 'oldest'])),
    transform(sort => sort === 'relevance' ? undefined : sort),
  ),
})

export type SearchUrlParams = InferOutput<typeof searchUrlSchema>

export function parseSearchQuery(query: Record<string, any>): Partial<SearchUrlParams> {
  const parsed = safeParse(searchUrlSchema, query)
  if (!parsed.success) {
    if (import.meta.client) {
      consola.warn('Invalid search query params', summarize(parsed.issues))
    }

    return {}
  }

  // Filter out undefined values to avoid adding defaults
  return Object.fromEntries(Object.entries(parsed.output).filter(([_, value]) => value !== undefined)) as Partial<SearchUrlParams>
}

/** Strip solr special characters from user search input, collapse whitespace */
export function sanitizeSearchQuery(input: string): string {
  return input.replace(STRIP_SEARCH_CHARACTERS, ' ').replace(/\s+/g, ' ').trim()
}
